import React, { useCallback, useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { btag, printMissingConfigError, pushToDataLayer, SiteConfigManager } from '@atlas/core';
import { staticRoutes } from '@atlas/routing';
import { Button, DefaultPopup, Label } from '@atlas/ui';

import ModalPortal from 'common/ModalPortal/ModalPortal';
import StaticBlock from 'common/StaticBlock/StaticBlock';
import { casinoFilterTypeMap, getFilter } from 'components/Pages/Casino/useCasinoFilter';
import { useSetNextShowPopupSignUpDelay } from 'hooks/useSetNextShowPopupSignUpDelay';
import SingleSignUp from 'src/components/Auth/SingleSignUp';

export const PopupSignUpDelay = () => {
  const { pathname } = useLocation();
  const { push: historyPush } = useHistory();

  const block = SiteConfigManager.getConfig('config.config.registration.mobile_popup_block');
  const isEnableRegistration = SiteConfigManager.getConfig('config.config.enable_registration');
  const signUpPopup = SiteConfigManager.getConfig('config.config.registration.showInPopup', false);
  const searchCasinoQuery = getFilter(casinoFilterTypeMap.name);

  const [showSingleSignUp, setShowSingleSignUp] = useState(false);

  const staticBlockOptions = useMemo(() => ({ source: block }), [block]);

  const redirectUrl = useMemo(() => {
    if (searchCasinoQuery) return `${pathname}?${casinoFilterTypeMap.name}=${searchCasinoQuery}`;
    return pathname;
  }, [pathname, searchCasinoQuery]);

  const handleClose = useCallback(() => {
    historyPush(redirectUrl);
  }, [historyPush, redirectUrl]);

  const handleSignUpClick = useCallback(() => {
    pushToDataLayer('registration_pop-up_CTA_click', btag);
  }, []);

  const renderFallback = useMemo(
    () => (
      <div className="sign-up-delay-fallback">
        <Label className="title-text" message="registration_popup_title" />
        <Label className="sub-title-text" message="registration_popup_description" />
      </div>
    ),
    [],
  );

  useSetNextShowPopupSignUpDelay();

  if (signUpPopup && showSingleSignUp) {
    return <SingleSignUp onClose={() => setShowSingleSignUp(false)} />;
  }

  if (!block) {
    printMissingConfigError('config.config.registration.mobile_popup_block');
  }

  return (
    <ModalPortal>
      <DefaultPopup className="popup-signup-delay" onClose={handleClose}>
        <div className="popup-signup-delay__content">
          <StaticBlock fallback={renderFallback} options={staticBlockOptions} />
        </div>

        <div className="popup-signup-delay__footer">
          <Link to={staticRoutes.signin}>
            <Button block label="Sign in" size="big" type="secondary" />
          </Link>

          {isEnableRegistration &&
            (signUpPopup ? (
              <Button
                className="delay-signup-button"
                label="Sign up"
                onClick={() => setShowSingleSignUp(true)}
                size="big"
                type="primary"
              />
            ) : (
              <Link onClick={handleSignUpClick} to={staticRoutes.signup}>
                <Button block label="Sign up" size="big" type="primary" />
              </Link>
            ))}
        </div>
      </DefaultPopup>
    </ModalPortal>
  );
};
